import React from 'react';
import { useTranslation } from 'react-i18next';

import { MontlyRateCalculationResponse } from '../../../store/_declarations/target';
import { useAppSelector } from '../../../store/hooks';
import { allInOneFormatter } from '../../../utils/formatters';
import { isVatInluded } from '../../../utils/marketSpecs';
import Button from '../Button';
import ErrorHandler from '../ErrorHandler';
import Font from '../Font';
import Icon from '../Icon';
import SpinnerComponent from '../Spinner';
import {
  ButtonWrapper,
  ConfirmElement,
  ConfirmWrapper,
  DownpaymentInfo,
  HeaderWidget,
  IconPosition,
  Overflow,
  Wrapper
} from './styles';

type WidgetWrapperProps = {
  children: JSX.Element | JSX.Element[] | string;
  hideConfirmButton?: boolean;
  onClickClose?: () => void;
  onClickConfirm?: () => void;
};

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  children,
  hideConfirmButton,
  onClickClose,
  onClickConfirm
}) => {
  const { t } = useTranslation();
  const { consumerPayload, selectedItem, widgetType } = useAppSelector(
    (state) => state.app
  );
  const { montlyRateCalculation, montlyRateCalculationPayloads } =
    useAppSelector((state) => state.financialCalculator);
  const { status, value } = useAppSelector((state) => state.auth);

  const withVat = isVatInluded(
    consumerPayload?.vehicle,
    consumerPayload?.context.customer,
    consumerPayload?.context.countryCode
  );

  const price = () => {
    if (selectedItem && montlyRateCalculation?.[selectedItem] === 'loading') {
      return <SpinnerComponent size={16} testid="product-cart-spinner" />;
    }
    if (montlyRateCalculation && selectedItem) {
      const maPrice = montlyRateCalculation?.[
        selectedItem
      ] as MontlyRateCalculationResponse;
      if (maPrice?.regularPayment?.amountExclTax) {
        const number = maPrice?.regularPayment?.amountExclTax;
        return allInOneFormatter(consumerPayload, number, t('Pricing'));
      }
    }
    return '-';
  };

  const error = status === 'failed' && !value;

  const selectedItemParameters: any =
    selectedItem && montlyRateCalculationPayloads?.[selectedItem]
      ? montlyRateCalculationPayloads[selectedItem]
      : {};

  const disableConfirm = !(
    selectedItem &&
    montlyRateCalculation?.[selectedItem] &&
    typeof montlyRateCalculation?.[selectedItem] === 'object'
  );

  return (
    <Wrapper $widgetType={widgetType}>
      <Overflow $widgetType={widgetType}>
        <>
          {widgetType === 'modal' ? (
            <HeaderWidget>
              <Font variant="heading3">{t('Header')}</Font>
              <IconPosition>
                <Icon
                  data-testid="close-button"
                  iconName="InterfaceClose"
                  onClick={onClickClose}
                />
              </IconPosition>
            </HeaderWidget>
          ) : null}
          {error ? (
            <ErrorHandler
              error={
                // eslint-disable-next-line no-constant-condition
                false // invalidManufacturerCode
                  ? t('ManufacturerCodeIncorrectMsg')
                  : t('ErrorMessage')
              }
            />
          ) : (
            children
          )}
        </>
      </Overflow>

      {!hideConfirmButton && widgetType === 'modal' ? (
        <ConfirmWrapper>
          <ButtonWrapper>
            <ConfirmElement>
              <DownpaymentInfo>
                <Font
                  customConfiguration={{ color: 'brand1', textAlign: 'right' }}
                  data-testid="monthlyPrice"
                  variant="heading4"
                >
                  {price()}
                </Font>
                <Font
                  customConfiguration={{
                    color: 'neutral400',
                    display: 'inline',
                    marginLeft: 10
                  }}
                  variant="bodyXSRegular"
                >
                  {withVat ? t('VATinc') : t('VATexcl')}
                </Font>
              </DownpaymentInfo>

              <DownpaymentInfo>
                <Font variant="bodySRegular">{t('Downpayment')}:</Font>
                <Font
                  customConfiguration={{
                    display: 'inline'
                  }}
                  variant="bodySBold"
                >
                  {selectedItemParameters?.widgetSelection?.parameters
                    ?.cashDeposit
                    ? allInOneFormatter(
                        consumerPayload,
                        selectedItemParameters?.widgetSelection?.parameters
                          ?.cashDeposit
                      )
                    : '-'}
                </Font>
              </DownpaymentInfo>
            </ConfirmElement>
            <ConfirmElement>
              <Button
                data-testid="confirm-button"
                disabled={disableConfirm}
                onClick={onClickConfirm}
                theme="light"
                type="button"
                variant="accent"
              >
                {t('Confirm')}
              </Button>
            </ConfirmElement>
          </ButtonWrapper>
        </ConfirmWrapper>
      ) : null}
    </Wrapper>
  );
};

export default WidgetWrapper;
